import { Tray } from '@phosphor-icons/react';
import { useEffect, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useSubmissions } from '../../api/submissions';
import { Button } from '../../components/Button';
import { Card } from '../../components/Card';
import { EmptyState } from '../../components/EmptyState';
import { Input } from '../../components/Input';
import { PageHeader } from '../../components/PageHeader';
import { SkeletonLoader } from '../../components/SkeletonLoader';
import { Table } from '../../components/Table';
import { useProjectBreadcrumbs } from '../../hooks/useProjectBreadcrumbs.js';
import { formatRelativeTime } from '../../utils/format';
import shared from '../shared.module.css';
import styles from './SubmissionListPage.module.css';

const PAGE_SIZE = 50;

function dataPreview(data: Record<string, unknown>): string {
	const keys = Object.keys(data);
	const visible = keys.slice(0, 3);
	const parts = visible.map((k) => {
		const raw = String(data[k]);
		const val = raw.length > 30 ? `${raw.slice(0, 30)}…` : raw;
		return `${k}: ${val}`;
	});
	if (keys.length > 3) {
		parts.push(`+${keys.length - 3} more`);
	}
	return parts.join(' · ');
}

export function SubmissionListPage() {
	const { id } = useParams<{ id: string }>();
	const navigate = useNavigate();
	const [searchParams, setSearchParams] = useSearchParams();

	const breadcrumbs = useProjectBreadcrumbs('Submissions');

	const appliedFilter = searchParams.get('form_id')?.trim() ?? '';
	const [formIdFilter, setFormIdFilter] = useState(appliedFilter);
	const [afterCursor, setAfterCursor] = useState<string | undefined>(undefined);

	useEffect(() => {
		setFormIdFilter(appliedFilter);
	}, [appliedFilter]);

	const params = {
		...(appliedFilter ? { form_id: appliedFilter } : {}),
		limit: PAGE_SIZE,
		...(afterCursor ? { after: afterCursor } : {}),
	};

	const { data: submissions, isLoading, error } = useSubmissions(id ?? '', params);

	function handleFilterSubmit(e: React.FormEvent) {
		e.preventDefault();
		setAfterCursor(undefined);
		const nextFilter = formIdFilter.trim();
		const nextSearchParams = new URLSearchParams(searchParams);
		if (nextFilter) {
			nextSearchParams.set('form_id', nextFilter);
		} else {
			nextSearchParams.delete('form_id');
		}
		setSearchParams(nextSearchParams);
	}

	function handleClearFilter() {
		setAfterCursor(undefined);
		setSearchParams((currentParams) => {
			const nextSearchParams = new URLSearchParams(currentParams);
			nextSearchParams.delete('form_id');
			return nextSearchParams;
		});
	}

	function handleLoadMore() {
		if (submissions && submissions.length > 0) {
			const last = submissions[submissions.length - 1];
			if (last) setAfterCursor(last.id);
		}
	}

	const columns = [
		{ key: 'form_id', label: 'Form ID', mono: true },
		{ key: 'identity', label: 'Identity' },
		{ key: 'submitted', label: 'Submitted' },
		{ key: 'data', label: 'Data Preview' },
	];

	const hasMore = submissions != null && submissions.length === PAGE_SIZE;

	return (
		<div>
			<PageHeader title="Submissions" breadcrumbs={breadcrumbs} />

			<div className={shared.content}>
				<form className={styles.filterBar} onSubmit={handleFilterSubmit}>
					<div className={styles.filterInput}>
						<Input
							label="Filter by form ID"
							name="form-id-filter"
							value={formIdFilter}
							onChange={setFormIdFilter}
							mono
							placeholder="Filter by form ID… (press Enter)"
						/>
					</div>
					{(formIdFilter || appliedFilter) && (
						<button type="button" className={styles.clearLink} onClick={handleClearFilter}>
							Clear
						</button>
					)}
				</form>

				<Card>
					{error ? (
						<p className={shared.errorMessage}>
							{error instanceof Error ? error.message : 'Failed to load submissions'}
						</p>
					) : (
						<>
							<Table>
								<Table.Header columns={columns} />
								<Table.Body>
									{isLoading ? (
										<SkeletonLoader rows={5} columns={4} />
									) : !submissions || submissions.length === 0 ? (
										<tr>
											<td colSpan={4}>
												<EmptyState
													icon={Tray}
													title="No submissions yet"
													description="Form submissions from lead-gen sites appear here."
												/>
											</td>
										</tr>
									) : (
										submissions.map((sub) => (
											<Table.Row
												key={sub.id}
												onClick={() => navigate(`/projects/${id}/submissions/${sub.id}`)}
											>
												<Table.Cell mono>{sub.form_id}</Table.Cell>
												<Table.Cell>
													<span className={styles.identityCell}>
														<span className={styles.identityBadge}>{sub.identity.type}</span>
														<span className={styles.identityValue}>{sub.identity.value}</span>
													</span>
												</Table.Cell>
												<Table.Cell mono>{formatRelativeTime(sub.submitted_at)}</Table.Cell>
												<Table.Cell>
													<span className={styles.dataPreview}>{dataPreview(sub.data)}</span>
												</Table.Cell>
											</Table.Row>
										))
									)}
								</Table.Body>
							</Table>

							{hasMore && (
								<div className={styles.loadMore}>
									<Button variant="secondary" onClick={handleLoadMore}>
										Load more
									</Button>
								</div>
							)}
						</>
					)}
				</Card>
			</div>
		</div>
	);
}
